import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { CloudArrowUpIcon, DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { uploadFile, formatFileSize } from '../utils/api'
import { MAX_FILE_SIZE } from '../../../shared/types'
import ProgressBar from './ProgressBar'
import UploadSuccess from './UploadSuccess'

interface FileUploadProps {
  onUploadSuccess?: (data: any) => void;
}

export default function FileUpload({ onUploadSuccess }: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadData, setUploadData] = useState<any>(null)
  const [showOptions, setShowOptions] = useState(false)
  const [options, setOptions] = useState({
    expiresIn: 168, // 7 days
    password: '',
    maxDownloads: undefined as number | undefined
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`File size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`)
        return
      }
      setSelectedFile(file)
      setUploadData(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: MAX_FILE_SIZE
  })

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadProgress(0)

    try {
      const data = await uploadFile({
        file: selectedFile,
        expiresIn: options.expiresIn,
        password: options.password || undefined,
        maxDownloads: options.maxDownloads,
        onProgress: setUploadProgress
      })

      if (data.success) {
        setUploadData(data.data)
        onUploadSuccess?.(data.data)
        toast.success('File uploaded successfully!')
      } else {
        toast.error(data.error || 'Upload failed')
      }
    } catch (error) {
      console.error('Upload error:', error)
      toast.error('Upload failed. Please try again.')
    } finally {
      setIsUploading(false)
    }
  }

  const resetUpload = () => {
    setSelectedFile(null)
    setUploadData(null)
    setUploadProgress(0)
    setShowOptions(false)
    setOptions({
      expiresIn: 168,
      password: '',
      maxDownloads: undefined
    })
  }

  if (uploadData) {
    return <UploadSuccess data={uploadData} onReset={resetUpload} />
  }

  return (
    <div className="card max-w-2xl mx-auto">
      <div className="space-y-6">
        {!selectedFile ? (
          <div
            {...getRootProps()}
            className={`upload-zone cursor-pointer ${isDragActive ? 'dragover' : ''}`}
          >
            <input {...getInputProps()} />
            <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              {isDragActive ? 'Drop the file here' : 'Drag & drop a file here'}
            </p>
            <p className="text-gray-500 mb-4">
              or click to select a file
            </p>
            <p className="text-sm text-gray-400">
              Maximum file size: {formatFileSize(MAX_FILE_SIZE)}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <DocumentIcon className="h-8 w-8 text-gray-400" />
                <div>
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                </div>
              </div>
              <button
                onClick={resetUpload}
                className="text-gray-400 hover:text-gray-600"
                disabled={isUploading}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <AnimatePresence>
              {isUploading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <ProgressBar progress={uploadProgress} />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-4">
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                disabled={isUploading}
              >
                {showOptions ? 'Hide' : 'Show'} Advanced Options
              </button>

              <AnimatePresence>
                {showOptions && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expires in (hours)
                      </label>
                      <select
                        value={options.expiresIn}
                        onChange={(e) => setOptions(prev => ({ ...prev, expiresIn: parseFloat(e.target.value) }))}
                        className="input-field"
                        disabled={isUploading}
                      >
                        <option value={0.033}>2 Minutes</option>
                        <option value={0.167}>10 Minutes</option>
                        <option value={1}>1 Hour</option>
                        <option value={24}>1 Day</option>
                        <option value={168}>7 Days</option>
                        <option value={720}>30 Days</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password (optional)
                      </label>
                      <input
                        type="password"
                        value={options.password}
                        onChange={(e) => setOptions(prev => ({ ...prev, password: e.target.value }))}
                        className="input-field"
                        placeholder="Enter password"
                        disabled={isUploading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max downloads (optional)
                      </label>
                      <input
                        type="number"
                        value={options.maxDownloads || ''}
                        onChange={(e) => setOptions(prev => ({ 
                          ...prev, 
                          maxDownloads: e.target.value ? parseInt(e.target.value) : undefined 
                        }))}
                        className="input-field"
                        placeholder="Unlimited"
                        min="1"
                        disabled={isUploading}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleUpload}
                disabled={isUploading}
                className="btn-primary flex-1"
              >
                {isUploading ? 'Uploading...' : 'Upload File'}
              </button>
              <button
                onClick={resetUpload}
                disabled={isUploading}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
} 